import {
  CarrierType,
  CarrierVisitDirection,
  CompanyResponseDto,
  OrderResponseDto,
  SealType,
  UpsertOrderListCommandV2,
} from '@planning/app/api'
import { csvFieldSchemaMap, IFieldConfig } from '@planning/config/csvHeaderConfig'
import { ContainerJourneyItemStore } from '@planning/pages/Issues/Stores/ContainerJourneyItemStore'
import {
  IOrderDto,
  OrderListUploadViewStoreV2,
} from '@planning/pages/Order/stores/OrderListUploadViewStoreV2'
import { getTemplateOptionalFieldSchema } from '@planning/pages/VesselVisit/Components/Upload/utils'
import { OrderItemStore } from '@planning/rt-stores/order/OrderItemStore'
import { TenantStore } from '@planning/rt-stores/tenant/TenantStore'
import { carrierVisitService, truckAppointmentService, truckVisitService } from '@planning/services'
import companyService from '@planning/services/companyService'
import containerJourneysService from '@planning/services/containerJourneys'
import _ from 'lodash'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import {
  exportToCsv,
  IDataExportConfig,
} from 'modules/ui/src/theme/components/table/utils/xlsxUtils'

export interface IYardBlock {
  id: string
  name: string
  bayNames: string[]
  rowNames: string[]
  maxTiers: number
}

export interface IImportContainerRequest {
  journeyId: number
  inboundOrderId: number | null
  containerId: number
  containerNumber: string
  length: number
  positionRaw: string
  blockId: string
  position: {
    bay: number
    row: number
    tier: number
  }
}

export enum CSVUploadAlertReason {
  ParserError = 'ParserError',
  UploadSuccess = 'UploadSuccess',
  GenericError = 'GenericError',
  YardValidationError = 'YardValidationError',
  ContainersOnTerminalError = 'ContainersOnTerminalError',
}

export interface ICSVUploadAlertParams {
  reason: CSVUploadAlertReason
  type: 'success' | 'error'
  message?: string
  data?: any
}

const POSITION_FIELD_SCHEMA: IFieldConfig = {
  name: 'Position',
  inputName: 'position',
  comparisonType: 'string',
}

export class CSVUploadViewStore {
  file: File | null = null
  isParsing: boolean = false
  selectedCustomer: CompanyResponseDto | null = null
  licensePlate: string = 'TRUCK123'
  isLoading: boolean = false
  availableBlocks: IYardBlock[] = []
  allCustomers: CompanyResponseDto[] = []

  private getYardBlocksByNames?: (names: string[]) => Promise<IYardBlock[]>
  private stackIn?: (requests: IImportContainerRequest[]) => Promise<void>
  private onAlert?: (params: ICSVUploadAlertParams) => void

  constructor(
    private orderItemStore: OrderItemStore,
    private orderListUploadStore: OrderListUploadViewStoreV2,
    private containerJourneyItemStore: ContainerJourneyItemStore,
    private tenantStore: TenantStore,
    options?: {
      getYardBlocksByNames?: (names: string[]) => Promise<IYardBlock[]>
      stackIn?: (requests: IImportContainerRequest[]) => Promise<void>
      onAlert?: (params: ICSVUploadAlertParams) => void
    },
  ) {
    this.getYardBlocksByNames = options?.getYardBlocksByNames
    this.stackIn = options?.stackIn
    this.onAlert = options?.onAlert

    makeObservable(this, {
      file: observable,
      isParsing: observable,
      selectedCustomer: observable,
      licensePlate: observable,
      isLoading: observable,
      availableBlocks: observable,
      allCustomers: observable,

      hasParsedOrders: computed,

      setFileState: action,
      setIsParsing: action,
      setSelectedCustomer: action,
      setLicensePlate: action,
      handleRemoveFile: action,
      reset: action,
      handleDrop: action,
      upsertOrderList: action,
      handleDownloadTemplate: action,
      fetchAllCustomers: action,
    })

    this.fetchAllCustomers()
  }

  get hasParsedOrders() {
    const updates = this.orderListUploadStore.orderUpdates
    return updates && (updates.created.length > 0 || updates.amended.length > 0)
  }

  setFileState = (file: File | null) => (this.file = file)
  setIsParsing = (isParsing: boolean) => (this.isParsing = isParsing)
  setSelectedCustomer = (customer: CompanyResponseDto | null) => (this.selectedCustomer = customer)
  setLicensePlate = (plate: string) => (this.licensePlate = plate)

  fetchAllCustomers = async () => {
    try {
      const customers = await companyService.getByPartialName('')
      runInAction(() => {
        this.allCustomers = customers
      })
    } catch (error) {
      console.error('[CSVUploadViewStore] Error fetching customers:', error)
    }
  }

  reset = () => {
    this.file = null
    this.isParsing = false
    this.selectedCustomer = null
    this.availableBlocks = []
    this.orderListUploadStore.reset()
  }

  handleRemoveFile = () => this.reset()

  handleDrop = async (acceptedFiles: File[]) => {
    this.orderListUploadStore.parsingStore.setUploadFailedReason()
    if (!acceptedFiles.length) return

    const selectedFile = acceptedFiles[0]
    this.setFileState(selectedFile)
    this.orderListUploadStore.setFile(selectedFile)

    runInAction(() => {
      this.orderListUploadStore.vesselVisitId = -1
      this.orderListUploadStore.direction = CarrierVisitDirection.Inbound
    })

    const unloCodes = this.tenantStore.tenant?.unloCodes.length
      ? this.tenantStore.tenant.unloCodes.map(i => i.name!)
      : ['DEHAM']
    this.orderListUploadStore.parsingStore.setUNLOCodes(unloCodes)

    this.setIsParsing(true)
    try {
      await this.orderListUploadStore.parseOrderList(false)
    } catch (error) {
      this.onAlert?.({ reason: CSVUploadAlertReason.ParserError, type: 'error' })
    } finally {
      this.setIsParsing(false)
    }
  }

  upsertOrderList = async () => {
    try {
      runInAction(() => {
        this.isLoading = true
      })

      const parsedOrders = this.orderListUploadStore.orderUpdates?.created || []
      if (parsedOrders.length === 0) return

      const positionMap = new Map<string, string>(
        parsedOrders.filter(o => o.containerNumber).map(o => [o.containerNumber, o.position || '']),
      )

      const isTerminalValid = await this.validateContainersNotOnTerminal(parsedOrders)
      if (!isTerminalValid) return

      const positions = Array.from(positionMap.values()).filter(p => !!p.trim())
      const isYardValid = await this.validateYardPositions(positions)
      if (!isYardValid) return

      // Group orders by customer
      const ordersByCustomer = _.groupBy(parsedOrders, o => (o.customerName || '').trim())
      const customerGroups = Object.entries(ordersByCustomer)

      // Validate all customers exist
      const unmatchedCustomers: string[] = []
      const customerMatches = new Map<string, CompanyResponseDto>()

      for (const [customerName, orders] of customerGroups) {
        const normalizedName = this.normalizeName(customerName)
        if (!normalizedName) {
          unmatchedCustomers.push('Empty Customer Name')
          continue
        }

        const match = this.allCustomers.find(
          c =>
            this.normalizeName(c.name) === normalizedName ||
            this.normalizeName(c.shortName || '') === normalizedName,
        )

        if (!match) {
          unmatchedCustomers.push(customerName)
        } else {
          customerMatches.set(customerName, match)
        }
      }

      if (unmatchedCustomers.length > 0) {
        throw new Error(`Customers not found: ${unmatchedCustomers.join(', ')}`)
      }

      // Create a single truck visit for the entire file
      const carrierVisitId = await this.createTruckVisit()

      // Process each customer group - only the order upload is repeated per customer
      for (const [customerName, orders] of customerGroups) {
        const customer = customerMatches.get(customerName)!
        await this.upsertOrdersToVisit(carrierVisitId, orders, customer.id)
      }

      // Finalize the visit lifecycle once
      const inboundOrders = await this.allowTruckEntryAndFetchOrders(carrierVisitId)

      if (inboundOrders.length > 0) {
        await this.processYardStackIn(inboundOrders, positionMap)
      }

      // Allow truck exit as the last step
      await truckVisitService.allowExit({ id: carrierVisitId })

      this.onAlert?.({ reason: CSVUploadAlertReason.UploadSuccess, type: 'success' })
      this.fetchNewContainerJourneys(parsedOrders)
      this.reset()
    } catch (error) {
      console.error('[POC Store] Error upsert:', error)
      this.onAlert?.({
        reason: CSVUploadAlertReason.GenericError,
        type: 'error',
        message: error instanceof Error ? error.message : String(error),
        data: error,
      })
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  private async processYardStackIn(
    inboundOrders: OrderResponseDto[],
    positionMap: Map<string, string>,
  ) {
    if (!this.stackIn) return

    const inboundOrderIds = inboundOrders.map(o => o.id)
    await this.containerJourneyItemStore.fetchByOrderIds(inboundOrderIds)

    const getIndex = (names: string[], name: string) =>
      names.findIndex(n => this.normalizeName(n) === this.normalizeName(name)) + 1

    const requests = inboundOrders
      .map((dbOrderData): IImportContainerRequest | null => {
        const orderId = dbOrderData.id
        const containerNumber = dbOrderData.containerNumber || ''
        const rawPosition = positionMap.get(containerNumber) || ''

        if (!rawPosition.trim()) return null

        const { rawBlock, rawBay, rawRow, rawTier } = this.parsePositionRaw(rawPosition)
        const targetBlock = this.getBlockByName(rawBlock)
        if (!targetBlock) return null

        const bayIndex = getIndex(targetBlock.bayNames, rawBay)
        const rowIndex = getIndex(targetBlock.rowNames, rawRow)

        if (bayIndex < 1 || rowIndex < 1) return null

        const journeyId =
          this.containerJourneyItemStore.journeysByInboundOrderId[orderId]?.data?.id ?? 0

        return {
          journeyId,
          inboundOrderId: orderId,
          containerId: dbOrderData.containerId ?? 0,
          containerNumber,
          length: dbOrderData.containerLength ?? 0,
          positionRaw: rawPosition,
          blockId: targetBlock.id,
          position: {
            bay: bayIndex,
            row: rowIndex,
            tier: parseInt(rawTier, 10) || 0,
          },
        }
      })
      .filter((req): req is IImportContainerRequest => req !== null)

    if (requests.length > 0) {
      await this.stackIn(requests)
    }
  }

  private normalizeName = (name: string) => name?.toLowerCase().replace(/\s/g, '') || ''

  private matchInList = (list: string[], value: string) => {
    const normalizedValue = (value || '').trim().toUpperCase()
    return list.some(item => (item || '').trim().toUpperCase() === normalizedValue)
  }

  private parsePositionRaw = (pos: string) => {
    const [rawBlock, rawBay, rawRow, rawTier] = (pos || '').split('.')
    return { rawBlock, rawBay, rawRow, rawTier }
  }

  private getBlockByName = (name: string) =>
    this.availableBlocks.find(b => this.normalizeName(b.name) === this.normalizeName(name))

  fetchYardBlocks = async (positions: string[]) => {
    if (!this.getYardBlocksByNames) return

    const blockNames = [...new Set(positions.map(p => (p || '').split('.')[0]).filter(Boolean))]
    try {
      const blocks = await this.getYardBlocksByNames(blockNames)
      runInAction(() => {
        this.availableBlocks = blocks
      })
    } catch (error) {
      console.error('[CSVUploadViewStore] Error fetching yard blocks:', error)
      throw error
    }
  }

  validateYardPositions = async (positions: string[]): Promise<boolean> => {
    if (positions.length === 0 || !this.getYardBlocksByNames) return true

    await this.fetchYardBlocks(positions)

    const errorMap = new Map<string, { key: string; params: any }>()

    positions.forEach(pos => {
      const { rawBlock, rawBay, rawRow, rawTier } = this.parsePositionRaw(pos)
      if (!rawBlock) return

      const targetBlock = this.getBlockByName(rawBlock)

      if (!targetBlock) {
        const err = { key: 'blockNotFound', params: { block: rawBlock } }
        errorMap.set(JSON.stringify(err), err)
        return
      }

      if (rawBay && targetBlock.bayNames && !this.matchInList(targetBlock.bayNames, rawBay)) {
        const err = { key: 'bayNotFound', params: { bay: rawBay, block: rawBlock } }
        errorMap.set(JSON.stringify(err), err)
      }

      if (rawRow && targetBlock.rowNames && !this.matchInList(targetBlock.rowNames, rawRow)) {
        const err = { key: 'rowNotFound', params: { row: rawRow, block: rawBlock } }
        errorMap.set(JSON.stringify(err), err)
      }

      if (rawTier && targetBlock.maxTiers !== undefined) {
        const tierValue = parseInt(rawTier, 10)
        if (isNaN(tierValue) || tierValue < 1 || tierValue > targetBlock.maxTiers) {
          const err = {
            key: 'invalidTier',
            params: { tier: rawTier, block: rawBlock, maxTiers: targetBlock.maxTiers },
          }
          errorMap.set(JSON.stringify(err), err)
        }
      }
    })

    if (errorMap.size > 0) {
      this.onAlert?.({
        reason: CSVUploadAlertReason.YardValidationError,
        type: 'error',
        data: Array.from(errorMap.values()),
      })
      return false
    }

    return true
  }

  private async validateContainersNotOnTerminal(ordersToAdd: any[]): Promise<boolean> {
    const containerNumbers = ordersToAdd
      .map((o: any) => o.containerNumber)
      .filter(Boolean) as string[]
    if (!containerNumbers.length) return true

    const journeys = await containerJourneysService.getByContainerNumbers(containerNumbers)
    const activeInboundOrderIds = journeys
      .map(j => j.inboundOrderId)
      .filter((id): id is number => !!id)

    if (!activeInboundOrderIds.length) return true

    await this.orderItemStore.fetchByIds(activeInboundOrderIds)

    const onTerminalContainers = Array.from(
      new Set(
        activeInboundOrderIds
          .map(id => this.orderItemStore.elements[id]?.data)
          .filter(order => order?.isOnTerminal)
          .map(order => order?.containerNumber)
          .filter(Boolean),
      ),
    )

    if (onTerminalContainers.length > 0) {
      this.onAlert?.({
        reason: CSVUploadAlertReason.ContainersOnTerminalError,
        type: 'error',
        message: onTerminalContainers.join(', '),
      })
      return false
    }
    return true
  }

  private async createTruckVisit(): Promise<number> {
    const truckCmd = {
      licensePlate: this.licensePlate,
      ordersWithDirection: [],
      ordersToBeCreated: [],
      nnrOrders: [],
    }
    const response = await truckAppointmentService.post(truckCmd)
    if (!response.truckVisitId) throw new Error('Failed to retrieve the created Truck Visit ID.')
    return response.truckVisitId
  }

  private async upsertOrdersToVisit(
    carrierVisitId: number,
    ordersToAdd: IOrderDto[],
    customerId: number,
  ) {
    const cmd: UpsertOrderListCommandV2 = {
      listName: this.orderListUploadStore.listName,
      carrierVisitId,
      direction: CarrierVisitDirection.Inbound,
      customerId,
      addOrders: ordersToAdd.map((order: IOrderDto) => ({
        ...order,
        hasSeals: !!order.seals,
        seals: order.seals
          ? order.seals.map(seal => ({ ...seal, type: seal.type ?? SealType.Unknown }))
          : [],
      })),
      addEmptyRailcars: [],
      addRemainOnBoard: this.orderListUploadStore.dataStore.remainOnBoard,
      addNonNumeric: this.orderListUploadStore.dataStore.nonNumeric,
      updateOrders: (this.orderListUploadStore.orderUpdates?.amended || []).map(a => a.update),
      deleteOrders: (this.orderListUploadStore.orderUpdates?.deleted || []).map(o => o.id),
    }

    await carrierVisitService.upsertOrderListV2(cmd)
  }

  private async allowTruckEntryAndFetchOrders(carrierVisitId: number) {
    const visitOrders = await carrierVisitService.orders(carrierVisitId)
    const inboundOrders = visitOrders.filter(o => o.direction === CarrierVisitDirection.Inbound)
    const inboundOrderIds = inboundOrders.map(o => o.id)

    await truckVisitService.allowEntry({
      id: carrierVisitId,
      inboundOrderIds: inboundOrderIds.length > 0 ? inboundOrderIds : undefined,
      outboundOrderIds: undefined,
    })

    return inboundOrders
  }

  private fetchNewContainerJourneys(ordersToAdd: IOrderDto[]) {
    const addedContainerNumbers = ordersToAdd.map(o => o.containerNumber)
    if (addedContainerNumbers.length) {
      this.containerJourneyItemStore.fetchByContainerNumbers(addedContainerNumbers)
    }
  }

  handleDownloadTemplate = () => {
    const carrierType = CarrierType.Truck
    const direction = CarrierVisitDirection.Inbound
    const optionalSchema = getTemplateOptionalFieldSchema(carrierType, direction)
    const schema = csvFieldSchemaMap.get(carrierType)

    if (!schema) return

    const combinedSchema = [...schema, ...(optionalSchema || [])]

    if (!combinedSchema.find(field => field.inputName === POSITION_FIELD_SCHEMA.inputName)) {
      combinedSchema.push(POSITION_FIELD_SCHEMA)
    }

    const dummyRow: Record<string, any> = {}
    const config: IDataExportConfig<any> = {}

    combinedSchema.forEach(field => {
      dummyRow[field.inputName] = ''
      config[field.inputName] = {
        header: field.name,
        type:
          field.comparisonType === 'boolean'
            ? 'boolean'
            : field.comparisonType === 'number'
              ? 'number'
              : 'string',
      }
    })

    exportToCsv('Order Upload Template', [dummyRow], config)
  }
}
